import "./PermissionsList.css"
import React, { useMemo } from "react";
import { Permission } from "../../../redux/models/permission.model";
import { groupPermissions, PermissionVerb } from "../../../utils/permissions";

// The permissions a role or user group grants, grouped by the area of the app they govern.
// Each row is the verb (view / update / delete…), a readable name and the API's description;
// the raw key sits in the tooltip for anyone matching it against code. Rendered inside a
// DataTable card (the page owns the toolbar): this is the scrolling body and its footer.

const VERB_LABEL: Record<PermissionVerb, string> = {
  view: 'View',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  assign: 'Assign',
  manage: 'Manage',
  other: 'Allow',
};

interface PermissionsListProps {
  permissions: Permission[];
  // Shown under "No Permissions"
  emptyHint?: string;
}

const PermissionsList: React.FC<PermissionsListProps> = ({permissions, emptyHint}) => {
  const groups = useMemo(() => groupPermissions(permissions), [permissions]);

  if (permissions.length === 0) {
    return (
      <div className="dt-scroll">
        <div className="dt-state">
          <div className="dt-empty-title">No Permissions</div>
          {emptyHint && <span className="lp-muted">{emptyHint}</span>}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dt-scroll pl">
        <div className="pl-groups">
          {groups.map((group) => (
            <section key={group.key} className="pl-group" aria-labelledby={`pl-group-${group.key}`}>
              <header className="pl-group-head">
                <h3 id={`pl-group-${group.key}`} className="pl-group-title">{group.label}</h3>
                <span className="pl-group-count">{group.permissions.length}</span>
              </header>
              <ul className="pl-list">
                {group.permissions.map(({key, label, verb, permission}) => (
                  <li key={key} className="pl-perm" title={key}>
                    <span className={`pl-verb ${verb}`}>{VERB_LABEL[verb]}</span>
                    <span className="pl-perm-text">
                      <span className="pl-perm-label">{label}</span>
                      {permission?.description
                        ? <span className="pl-perm-desc">{permission.description}</span>
                        : <span className="pl-perm-desc pl-perm-key">{key}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <div className="dt-foot">
        <span className="dt-showing">
          {permissions.length.toLocaleString()} {permissions.length === 1 ? 'Permission' : 'Permissions'}
          {groups.length > 1 && ` across ${groups.length} areas`}
        </span>
      </div>
    </>
  );
};

export default PermissionsList;
